// src/pages/Login.jsx
// Members, sign-in page.
// A controlled username and password form that authenticates through the
// AuthProvider. On success it sends the user wherever they were headed
// (set by ProtectedRoute) or to the home page.

import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where to land after a successful login: the page the user was sent here from,
  // or the home page as a fallback.
  const from = location.state?.from?.pathname || "/";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login({ username, password });
      navigate(from, { replace: true });
    } catch (err) {
      // The backend sends { success: false, error: "..." }. Fall back to a
      // generic message if the request never reached it.
      setError(
        err.response?.data?.error || "Unable to sign in. Please try again.",
      );
      setSubmitting(false);
    }
  }

  const inputClasses =
    "w-full bg-transparent border-0 border-b border-ink-20 py-3 text-body text-ink focus:outline-none focus:border-ink transition-colors";

  return (
    <>
      {/* Page header */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-12 md:pt-28 md:pb-16">
        <p className="label-mono">Members</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          Sign In
        </h1>
        <p className="text-body text-ink-70 leading-relaxed mt-6 max-w-2xl">
          Sign in to reach your account, and for editors, the dashboard.
        </p>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* Form */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="max-w-md">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label htmlFor="username" className="label-mono block mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className={inputClasses}
              />
            </div>

            <div>
              <label htmlFor="password" className="label-mono block mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className={inputClasses}
              />
            </div>

            {error && <p className="text-small text-oxblood">{error}</p>}

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="label-mono border-b border-ink hover:text-oxblood hover:border-oxblood transition-colors py-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink disabled:hover:border-ink"
              >
                {submitting ? "Signing in..." : "Sign in →"}
              </button>
            </div>
          </form>

          <p className="text-small text-ink-70 mt-10">
            No account yet?{" "}
            <Link
              to="/create-account"
              className="text-ink border-b border-ink-20 hover:text-oxblood hover:border-oxblood transition-colors"
            >
              Create one
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
